import { supabase } from './supabase';
import { gatewayAuthHeaders } from './gateway-auth';
import { getNtpNow } from '@/utils/ntp-sync';
import type {
  ArcContact,
  ArcEvent,
  ArcCallLog,
  CidCode,
  AlarmSite,
  AlarmSiteExtended,
  SiteScheduleEntry,
  SiteSchedule,
  ScheduleDay,
} from '@asi/shared';

// ─── Shared CID Description Lookup ───────────────────────────

export const CID_DESCRIPTIONS: Record<string, string> = {
  '100': 'Emergencia médica', '101': 'Auxilio personal',
  // Hikvision AX PRO uses 103 as its generic interior/follower zone trip
  // (confirmed: 100/101/102 never appear in production, 103 is used by thousands
  // of motion sensors and door/window contacts across the whole fleet).
  '103': 'Alarma de zona interior',
  '110': 'Alarma de fuego',
  '111': 'Humo detectado', '112': 'Combustión detectada', '114': 'Calor detectado',
  '120': 'Pánico', '121': 'Duress (coacción)', '122': 'Pánico silencioso',
  '123': 'Pánico audible', '126': 'Amenaza / Hold-up',
  '127': 'Alarma Especial (Pánico App)', '128': 'Pánico no verificado',
  // Hikvision AX PRO reports CID 129 for the physical panic button zone type
  // (confirmed across 30+ panels) — NOT the generic Ademco "thermal" meaning.
  '129': 'Pánico',
  '130': 'Robo', '131': 'Zona perimetral',
  '132': 'Interior', '133': 'Entrada 24h', '134': 'Entrada/Salida',
  '137': 'Tamper', '140': 'Alarma general', '151': 'Gas detectado',
  '162': 'CO detectado', '300': 'Problema del sistema', '301': 'Falla energía AC',
  '302': 'Batería baja', '303': 'Error checksum RAM', '304': 'Error checksum ROM',
  '305': 'Reset del sistema', '306': 'Programación cambiada', '307': 'Falla auto-prueba',
  '309': 'Falla prueba batería', '310': 'Falla a tierra', '311': 'Falla de batería', '318': 'Agotamiento de energía',
  '312': 'Sobrecorriente fuente', '320': 'Falla sirena/relé', '331': 'Loop abierto',
  '332': 'Loop corto-circuito', '333': 'Problema expansor', '334': 'Falla repetidor',
  '341': 'Sabotaje módulo expansor', '344': 'Interferencia RF', '350': 'Falla comunicaciones',
  '351': 'Falla línea telefónica', '354': 'Falla al comunicar evento',
  '356': 'Pérdida sondeo central', '370': 'Loop de protección', '373': 'Tamper zona fuego',
  '374': 'Error de salida', '380': 'Falla sensor', '381': 'Pérdida supervisión RF',
  '383': 'Tamper sensor', '384': 'Batería baja sensor', '400': 'Apertura/Cierre',
  '401': 'Armado Away', '402': 'Armado Away parcial', '403': 'Desarmado',
  '404': 'Apertura tardía', '406': 'Cancelar', '407': 'Acceso remoto',
  '408': 'Armado rápido', '409': 'Armado keyswitch', '411': 'Solicitud callback',
  '413': 'Acceso fallido', '421': 'Acceso denegado', '423': 'Acceso forzado',
  '426': 'Puerta sostenida abierta', '441': 'Armado Stay', '442': 'Armado Stay parcial',
  '456': 'Armado parcial', '459': 'Cierre reciente', '461': 'Código incorrecto',
  '463': 'Re-armado post-alarma', '501': 'Lector deshabilitado', '556': 'Cambio de red', '570': 'Bypass de zona',
  '571': 'Unbypass de zona', '572': 'Bypass zona 24h', '573': 'Bypass robo',
  '601': 'Test manual', '602': 'Test periódico', '604': 'Prueba de incendio',
  '606': 'Test comunicación', '607': 'Walk Test iniciado', '608': 'Walk Test finalizado',
  '611': 'Punto probado OK', '616': 'Solicitud de servicio', '621': 'Log reiniciado',
  '625': 'Fecha/Hora reiniciada', '700': 'Iniciado por usuario', '720': 'Prueba telefónica',
  '810': 'Emergencia desde app (SOS)', '811': 'Incendio desde app', '822': 'Fallo de armado',
  '862': 'Teclado bloqueado', '865': 'Tag/tarjeta no registrada',
  '900': 'Problema sin clasificar',
  // Hikvision AX PRO 9xx: communication / device troubles (verified 2026-10-22 against the panels'
  // own description field) — NOT arm/disarm methods as previously assumed.
  '914': 'Detector inalámbrico desconectado', '915': 'Batería baja de detector',
  '916': 'Módulo de expansión o teclado desconectado', '920': 'Datos celulares desconectados',
  '921': 'Falla de tarjeta SIM', '922': 'Falla de Wi-Fi', '923': 'Falla de radio inalámbrica (RF)',
  '925': 'Batería baja de control remoto', '930': 'Conflicto de dirección IP', '931': 'Red desconectada',
  '975': 'Detector agregado/eliminado', '977': 'Módulo agregado/eliminado', '979': 'Dispositivo inalámbrico agregado/eliminado',
  '988': 'Inactivación permanente externa', '989': 'Alarma médica silenciosa',
  '455': 'Auto-armado fallido', '467': 'Código incorrecto en teclado', '847': 'Emergencia médica desde app',
};

/** Get Spanish description for a CID code (handles 4-digit HPP QCCC format) */
export function getCidDescription(code: string): string {
  const base = code.length === 4 && /^[13]\d{3}$/.test(code) ? code.slice(1) : code;
  return CID_DESCRIPTIONS[base] ?? CID_DESCRIPTIONS[code] ?? `Código CID ${code}`;
}

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL as string | undefined;

// ─── Gateway Proxy ────────────────────────────────────────────

async function callGateway<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  if (!GATEWAY_URL) {
    throw new Error('Gateway URL not configured');
  }

  const url = `${GATEWAY_URL}${path}`;
  const init: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...gatewayAuthHeaders(),
      ...options.headers,
    },
  };

  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, string>;
    throw new Error(err.error ?? `Gateway error ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ─── ARC Contacts CRUD ────────────────────────────────────────

/** Fetch all contacts for a site, including the primary contact from alarm_sites */
export async function fetchContacts(siteId: string): Promise<ArcContact[]> {
  // 1. Fetch additional contacts from arc_contacts
  const { data: arcContacts, error } = await supabase
    .from('arc_contacts')
    .select('*')
    .eq('site_id', siteId)
    .order('priority', { ascending: true });

  if (error) throw new Error(error.message);
  
  // 2. Fetch the primary contact from alarm_sites
  const { data: site } = await supabase
    .from('alarm_sites')
    .select('id, contact_name, contact_phone, contact_email')
    .eq('id', siteId)
    .single();
  
  const contacts: ArcContact[] = [];
  
  // Add primary contact from site if it exists
  if (site?.contact_name) {
    contacts.push({
      id: `primary-${site.id}`, // Virtual ID for primary contact
      site_id: siteId,
      name: site.contact_name,
      phone: site.contact_phone,
      mobile: null,
      email: site.contact_email,
      relation: 'owner' as const,
      relation_notes: 'Contacto principal del sitio',
      priority: 0, // Always first
      can_verify: true,
      can_dispatch: true,
      can_arm_disarm: true,
      notify_alarms: true,
      notify_arming: true,
      notify_tamper: true,
      notify_low_battery: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }
  
  // Add arc_contacts
  contacts.push(...(arcContacts ?? []));
  
  return contacts;
}

/** Create a new contact */
export async function createContact(
  contact: Omit<ArcContact, 'id' | 'created_at' | 'updated_at'>
): Promise<ArcContact> {
  const { data, error } = await supabase
    .from('arc_contacts')
    .insert(contact)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/** Update a contact */
export async function updateContact(
  id: string,
  updates: Partial<Omit<ArcContact, 'id' | 'created_at' | 'updated_at'>>
): Promise<ArcContact> {
  const { data, error } = await supabase
    .from('arc_contacts')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/** Delete a contact */
export async function deleteContact(id: string): Promise<void> {
  const { error } = await supabase
    .from('arc_contacts')
    .delete()
    .eq('id', id);

  if (error) throw new Error(error.message);
}

/** Reorder contacts (update priorities) */
export async function reorderContacts(
  siteId: string,
  contactIds: string[]
): Promise<void> {
  for (let i = 0; i < contactIds.length; i++) {
    const { error } = await supabase
      .from('arc_contacts')
      .update({ priority: i + 1 })
      .eq('id', contactIds[i]);

    if (error) throw new Error(error.message);
  }
}

// ─── ARC Events ───────────────────────────────────────────────

/** Fetch recent ARC events */
export async function fetchArcEvents(
  limit = 100,
  filters?: {
    siteId?: string;
    severity?: string;
    acknowledged?: boolean;
    startDate?: string;
    endDate?: string;
  }
): Promise<ArcEvent[]> {
  let query = supabase
    .from('arc_events')
    .select('*, alarm_sites(name)')
    .order('received_at', { ascending: false })
    .limit(limit);

  if (filters?.siteId) {
    query = query.eq('site_id', filters.siteId);
  }
  if (filters?.severity) {
    query = query.eq('severity', filters.severity);
  }
  if (filters?.acknowledged === true) {
    query = query.not('acknowledged_at', 'is', null);
  } else if (filters?.acknowledged === false) {
    query = query.is('acknowledged_at', null);
  }
  if (filters?.startDate) {
    query = query.gte('received_at', filters.startDate);
  }
  if (filters?.endDate) {
    query = query.lte('received_at', filters.endDate);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  // Flatten alarm_sites.name → site_name so the ribbon skips individual enrichment queries.
  return (data ?? []).map((e: any) => ({
    ...e,
    site_name: (e.alarm_sites as { name?: string } | null)?.name ?? null,
    alarm_sites: undefined,
  })) as ArcEvent[];
}

/**
 * Fetch ALL pending (unacknowledged) events regardless of recency.
 * Used to reconcile events that may have been missed during gateway downtime or
 * browser standby — those events are in the DB but not in the in-memory store.
 *
 * Internally chunks the query in pages of `PAGE_SIZE` rows using `range()` to
 * bypass the PostgREST `max_rows = 1000` cap. The outer `limit` is a safety net
 * (browser memory). The header badge uses the real COUNT from the DB via
 * fetchPendingCount(), so the operator always knows the true backlog.
 */
export async function fetchPendingArcEvents(limit = 5000): Promise<ArcEvent[]> {
  const PAGE_SIZE = 1000; // PostgREST default max_rows
  const out: ArcEvent[] = [];
  let from = 0;
  while (out.length < limit) {
    const to = Math.min(from + PAGE_SIZE, limit) - 1;
    const { data, error } = await supabase
      .from('arc_events')
      .select('*, alarm_sites(name)')
      .is('acknowledged_at', null)
      .order('received_at', { ascending: false })
      .range(from, to);
    if (error) throw new Error(error.message);
    // Flatten alarm_sites.name → site_name so the ribbon skips individual enrichment queries.
    const batch = (data ?? []).map((e: any) => ({
      ...e,
      site_name: (e.alarm_sites as { name?: string } | null)?.name ?? null,
      alarm_sites: undefined,
    }));
    out.push(...batch);
    if (batch.length < to - from + 1) break; // no more rows
    from = to + 1;
  }
  return out;
}

/** Acknowledge an ARC event */
export async function acknowledgeArcEvent(eventId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  const { error } = await supabase
    .from('arc_events')
    .update({
      acknowledged_at: new Date().toISOString(),
      acknowledged_by: user.id,
    })
    .eq('id', eventId);

  if (error) throw new Error(error.message);
}

/** Resolve an ARC event */
export async function resolveArcEvent(
  eventId: string,
  resolution: string
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  const { error } = await supabase
    .from('arc_events')
    .update({
      resolved_at: new Date().toISOString(),
      resolved_by: user.id,
      resolution,
    })
    .eq('id', eventId);

  if (error) throw new Error(error.message);
}

/**
 * Process an ARC event in one step: acknowledge + resolve simultaneously.
 * This moves the event directly from Alarmas to the history (bitácora).
 */
export async function processArcEvent(
  eventId: string,
  resolution?: string,
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  const now = new Date().toISOString();
  const { error } = await supabase
    .from('arc_events')
    .update({
      acknowledged_at: now,
      acknowledged_by: user.id,
      resolved_at: now,
      resolved_by: user.id,
      resolution: resolution?.trim() || null,
      response_started_at: now,
      response_completed_at: now,
    })
    .eq('id', eventId);

  if (error) throw new Error(error.message);
}

/** Batch process (acknowledge + resolve) using a server-side RPC.
 * The function runs SECURITY DEFINER so the authorization check happens once,
 * not per-row — making large batches (tens of thousands of events) reliable.
 * @returns number of rows updated
 */
export async function processBatchByFilter(options: {
  siteId?: string;
  category?: string;
  resolution: string;
  /** When set, processes only these specific event IDs (ignores siteId/category on the server). */
  eventIds?: string[];
}): Promise<number> {
  const { data, error } = await supabase.functions.invoke('process-arc-batch', {
    body: {
      siteId:     options.eventIds?.length ? null : (options.siteId ?? null),
      category:   (options.category && options.category !== 'ALL') ? options.category : null,
      resolution: options.resolution.trim(),
      eventIds:   options.eventIds?.length ? options.eventIds : undefined,
    },
  });

  if (error) {
    // FunctionsHttpError carries the actual response body — extract it for a useful message
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body = await (error as any).context?.json?.().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? error.message);
  }
  const result = data as { processed?: number; error?: string };
  if (result?.error) throw new Error(result.error);
  return result?.processed ?? 0;
}

/** @deprecated Use processBatchByFilter — kept for any remaining ID-based callers */
export async function processBatchArcEvents(
  eventIds: string[],
  resolution: string,
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  const now = new Date().toISOString();
  const payload = {
    acknowledged_at: now,
    acknowledged_by: user.id,
    resolved_at: now,
    resolved_by: user.id,
    resolution: resolution.trim(),
    response_started_at: now,
    response_completed_at: now,
  };

  const CHUNK = 50;
  for (let i = 0; i < eventIds.length; i += CHUNK) {
    const chunk = eventIds.slice(i, i + CHUNK);
    const { error } = await supabase
      .from('arc_events')
      .update(payload)
      .in('id', chunk);
    if (error) throw new Error(error.message);
  }
}

// ─── CID Codes ────────────────────────────────────────────────

/** Fetch all CID codes */
export async function fetchCidCodes(): Promise<CidCode[]> {
  const { data, error } = await supabase
    .from('cid_codes')
    .select('*')
    .order('code');

  if (error) throw new Error(error.message);
  return data ?? [];
}

/** Fetch CID code by code number */
export async function getCidCode(code: string): Promise<CidCode | null> {
  const { data, error } = await supabase
    .from('cid_codes')
    .select('*')
    .eq('code', code)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data;
}

// ─── Call Log ─────────────────────────────────────────────────

/** Fetch call log for an event */
export async function fetchCallLog(eventId: string): Promise<ArcCallLog[]> {
  const { data, error } = await supabase
    .from('arc_call_log')
    .select('*')
    .eq('event_id', eventId)
    .order('called_at', { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/** Log a verification call */
export async function logCall(
  call: Omit<ArcCallLog, 'id'>
): Promise<ArcCallLog> {
  const { data, error } = await supabase
    .from('arc_call_log')
    .insert(call)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// ─── MQ Polling ───────────────────────────────────────────────

export interface MqPollResult {
  batchId: string | null;
  list: Array<{
    msgType: string;
    alarmData: Record<string, unknown> | string;  // May come as escaped JSON string
  }>;
}

/** HPP MQ AlarmData structure (from HPP documentation) */
interface HppAlarmData {
  deviceSerial: string;
  cidEvent?: string;       // CID code as string e.g. "1130"
  zoneNo?: number;
  zoneName?: string;
  partition?: number;
  timestamp?: number;      // Unix timestamp
  alarmLevel?: number;     // 1=info, 2=warning, 3=alarm, 4=critical
  // Additional fields that may appear
  userNo?: number;
  userName?: string;
  triggerTime?: string;    // ISO string format
  eventType?: string;      // Some events use this
  CIDEvent?: {             // Alternative CID format
    code: number;
    qualifier?: string;
    zone?: number;
    user?: number;
    partition?: number;
  };
}

/** Map CID code to category (must match cid_event_category enum)
 *
 * HPP sends 4-digit codes in QCCC format (qualifier prefix + 3-digit CID code).
 * Strip the prefix before classifying.
 */
function cidCodeToCategory(code: number): string {
  // Strip 4-digit HPP qualifier prefix: 1xxx = new event, 3xxx = restore
  const c = code >= 1000 ? code % 1000 : code;
  // 1xx: Alarms (medical, fire, panic, burglary) - Event
  if (c >= 100 && c < 200) return 'alarm';
  // 2xx: Supervisory (usually alarm-related)
  if (c >= 200 && c < 300) return 'alarm';
  // 3xx: Troubles (system problems, low battery, AC fail)
  if (c >= 300 && c < 400) return 'trouble';
  // 4xx: Open/Close (arm/disarm)
  if (c >= 400 && c < 500) return 'open_close';
  // 5xx: Bypass
  if (c >= 500 && c < 600) return 'bypass';
  // 6xx: Test (periodic test, walk test)
  if (c >= 600 && c < 700) return 'test';
  // 9xx: Hikvision vendor-specific arm/disarm
  if (c >= 900 && c < 1000) return 'open_close';
  // Default
  return 'system';
}

/** Map CID code to severity (must match alarm_event_severity enum) */
function cidCodeToSeverity(code: number): string {
  // Strip 4-digit HPP qualifier prefix
  const c = code >= 1000 ? code % 1000 : code;
  // Critical: Fire (110-119), Medical (100-109), Panic (120-129), Burglary (130-149)
  if (c >= 100 && c < 150) return 'CRITICAL';
  // Alarm: Duress, Hold-up (150-199), Supervisory (200-299)
  if (c >= 150 && c < 300) return 'ALARM';
  // Warning: Troubles (300-399)
  if (c >= 300 && c < 400) return 'WARNING';
  // Info: Arm/Disarm (400-499), Bypass (500-599), Test (600-699)
  return 'INFO';
}

/**
 * Process MQ events and save to arc_events table.
 * Returns the number of events saved.
 */
export async function processAndSaveMqEvents(
  events: Array<{ msgType: string; alarmData: Record<string, unknown> | string }>,
  batchId: string | null
): Promise<{ saved: number; errors: number }> {
  let saved = 0;
  let errors = 0;

  for (const msg of events) {
    try {
      // alarmData may come as escaped JSON string - parse it
      let rawData = msg.alarmData;
      if (typeof rawData === 'string') {
        try {
          rawData = JSON.parse(rawData);
        } catch {
          continue;
        }
      }
      
      const data = rawData as unknown as HppAlarmData;
      const deviceSerial = data.deviceSerial;
      
      if (!deviceSerial) {
        console.warn('[ARC] Event dropped: no deviceSerial', rawData);
        continue;
      }

      // Only process CID events
      if (data.eventType !== 'cidEvent') {
        console.warn(`[ARC] Event dropped (deviceSerial=${deviceSerial}): eventType="${data.eventType}" is not "cidEvent"`, rawData);
        continue;
      }

      // Extract CID code
      let cidCode: number = 0;
      let qualifier = 'E';
      let zoneNo: number | null = null;
      let partitionNo = 1;
      let userCode: string | null = null;

      if (data.CIDEvent?.code) {
        cidCode = data.CIDEvent.code;
        qualifier = data.CIDEvent.qualifier ?? 'E';
        zoneNo = data.CIDEvent.zone ?? null;
        partitionNo = data.CIDEvent.partition ?? 1;
        userCode = data.CIDEvent.user ? String(data.CIDEvent.user) : null;
      } else if (data.cidEvent) {
        cidCode = parseInt(data.cidEvent, 10);
      }

      if (!cidCode) {
        console.warn(`[ARC] Event dropped (deviceSerial=${deviceSerial}): cidCode=0, could not parse`, data);
        continue;
      }

      // Lookup panel by serial number
      const { data: panel } = await supabase
        .from('alarm_panels')
        .select('id, site_id')
        .eq('serial_number', deviceSerial)
        .single();

      if (!panel) {
        console.warn(`[ARC] Panel "${deviceSerial}" not found in alarm_panels — event will be saved without site/client association`);
      }
      // Cap received_at at NOW to prevent future-dated events from devices with wrong clocks
      const now = Date.now();
      let receivedAt: string;
      if (data.timestamp) {
        const t = data.timestamp * 1000;
        receivedAt = new Date(Math.min(t, now)).toISOString();
      } else if (data.triggerTime) {
        const t = new Date(data.triggerTime).getTime();
        receivedAt = new Date(Math.min(t, now)).toISOString();
      } else {
        receivedAt = new Date().toISOString();
      }

      // Insert into arc_events
      const { error } = await supabase.from('arc_events').insert({
        panel_id: panel?.id ?? null,
        site_id: panel?.site_id ?? null,
        cid_code: String(cidCode),
        cid_qualifier: qualifier,
        event_category: cidCodeToCategory(cidCode),
        zone_no: zoneNo,
        zone_name: data.zoneName ?? null,
        partition_no: partitionNo,
        user_code: userCode,
        severity: cidCodeToSeverity(cidCode),
        received_at: receivedAt,
        raw_message: data,  // Store parsed object, not escaped string
        hpp_batch_id: batchId,
      });

      if (error) {
        console.error('[ARC] Failed to save event:', error.message);
        errors++;
      } else {
        saved++;
      }
    } catch (err) {
      console.error('[ARC] Event processing error:', err);
      errors++;
    }
  }

  return { saved, errors };
}

/** Poll for new MQ messages */
export async function pollMqMessages(timeout = 20000): Promise<MqPollResult> {
  return callGateway('/api/hpp/mq/poll', {
    method: 'POST',
    body: JSON.stringify({ timeout }),
  });
}

/** Confirm MQ batch receipt */
export async function confirmMqBatch(batchId: string): Promise<void> {
  await callGateway('/api/hpp/mq/confirm', {
    method: 'POST',
    body: JSON.stringify({ batchId }),
  });
}

/** Start MQ subscription */
export async function startMqSubscription(): Promise<{ success: boolean }> {
  return callGateway('/api/hpp/mq/subscribe', {
    method: 'POST',
    body: JSON.stringify({ subType: 1, subMode: 'all' }),
  });
}

/** Stop MQ subscription */
export async function stopMqSubscription(): Promise<{ success: boolean }> {
  return callGateway('/api/hpp/mq/unsubscribe', {
    method: 'POST',
    body: JSON.stringify({ subType: 0, subMode: 'all' }),
  });
}

// ─── Site Extended Info ───────────────────────────────────────
/** Fetch full site detail including ARC extended fields */
export async function fetchSiteDetail(siteId: string): Promise<AlarmSiteExtended | null> {
  const { data, error } = await supabase
    .from('alarm_sites')
    .select('*, contacts:arc_contacts(*)')
    .eq('id', siteId)
    .single();

  if (error) return null;
  return data as AlarmSiteExtended;
}
/** Update site ARC fields */
export async function updateSiteArcInfo(
  siteId: string,
  info: {
    account_number?: string;
    contract_start?: string;
    contract_end?: string;
    service_level?: 'basic' | 'premium' | 'enterprise';
    response_time_minutes?: number;
    special_instructions?: string;
    password_phrase?: string;
  }
): Promise<AlarmSite> {
  const { data, error } = await supabase
    .from('alarm_sites')
    .update({ ...info, updated_at: new Date().toISOString() })
    .eq('id', siteId)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// ─── Statistics ───────────────────────────────────────────────

/** Get event statistics for dashboard */
export async function getEventStats(
  startDate?: string,
  endDate?: string
): Promise<{
  total: number;
  byCategory: Record<string, number>;
  bySeverity: Record<string, number>;
  responseTimeAvg: number | null;
}> {
  const start = startDate ?? new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const end = endDate ?? new Date().toISOString();

  const { data, error } = await supabase.rpc('get_arc_event_stats', {
    p_start: start,
    p_end: end,
  });

  if (error) throw new Error(error.message);

  const result = data as {
    total: number;
    byCategory: Record<string, number>;
    bySeverity: Record<string, number>;
    responseTimeAvg: number | null;
  };

  return {
    total: result.total ?? 0,
    byCategory: result.byCategory ?? {},
    bySeverity: result.bySeverity ?? {},
    responseTimeAvg: result.responseTimeAvg ?? null,
  };
}

// ─── Realtime Subscription ────────────────────────────────────

/** Subscribe to new ARC events in realtime.
 *  Pass a unique channelName when subscribing from multiple components
 *  to avoid Supabase channel conflicts (default is the app-level channel). */
export function subscribeToArcEvents(
  callback: (event: ArcEvent) => void,
  onUpdate?: (event: Partial<ArcEvent> & { id: string }) => void,
  channelName = 'arc-events-realtime'
): () => void {
  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'arc_events' },
      (payload) => { callback(payload.new as ArcEvent); }
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'arc_events' },
      (payload) => {
        // Only propagate status-change fields to avoid full re-render on every update
        const u = payload.new as ArcEvent;
        onUpdate?.({ id: u.id, acknowledged_at: u.acknowledged_at, resolved_at: u.resolved_at, resolution: u.resolution });
      }
    )
    .subscribe();

  return () => { supabase.removeChannel(channel); };
}

// ─── Bitácora / History ───────────────────────────────────────

export interface HistoryFilters {
  dateFrom: string;
  dateTo: string;
  siteId: string;
  severity: string;
  category: string;
  status: 'all' | 'pending' | 'acknowledged' | 'resolved';
  search?: string;
}

/** History event row: arc_event + inline site name + operator profiles */
export interface HistoryEvent extends ArcEvent {
  alarm_sites: { name: string; account_number: string | null } | null;
  ack_profile: { full_name: string | null; email: string } | null;
  res_profile: { full_name: string | null; email: string } | null;
}

/** Fetch history events with filters and pagination */
export async function fetchHistoryEvents(
  filters: HistoryFilters,
  page = 0,
  pageSize = 100
): Promise<{ data: HistoryEvent[]; total: number }> {
  // If there is a text search, find matching site IDs first so we can include them in the OR
  let matchingSiteIds: string[] | null = null;
  if (filters.search) {
    const { data: siteMatches } = await supabase
      .from('alarm_sites')
      .select('id')
      .ilike('name', `%${filters.search}%`)
      .limit(200);
    matchingSiteIds = (siteMatches ?? []).map((s: { id: string }) => s.id);
  }

  let query = supabase
    .from('arc_events')
    .select(
      '*, alarm_sites ( name, account_number ), ack_profile:profiles!acknowledged_by ( full_name, email ), res_profile:profiles!resolved_by ( full_name, email )',
      { count: 'exact' }
    )
    .order('received_at', { ascending: false })
    .range(page * pageSize, (page + 1) * pageSize - 1);

  if (filters.dateFrom) {
    query = query.gte('received_at', `${filters.dateFrom}T00:00:00`);
  }
  if (filters.dateTo) {
    query = query.lte('received_at', `${filters.dateTo}T23:59:59`);
  }
  if (filters.siteId) {
    query = query.eq('site_id', filters.siteId);
  }
  if (filters.severity) {
    query = query.eq('severity', filters.severity);
  }
  if (filters.category) {
    query = query.eq('event_category', filters.category);
  }
  if (filters.status === 'pending') {
    query = query.is('acknowledged_at', null);
  } else if (filters.status === 'acknowledged') {
    query = query.not('acknowledged_at', 'is', null).is('resolved_at', null);
  } else if (filters.status === 'resolved') {
    query = query.not('resolved_at', 'is', null);
  }

  if (filters.search) {
    const s = filters.search.replace(/[%_]/g, '\\$&'); // escape special chars
    const orParts = [
      `cid_code.ilike.%${s}%`,
      `zone_name.ilike.%${s}%`,
      `resolution.ilike.%${s}%`,
    ];
    if (matchingSiteIds && matchingSiteIds.length > 0) {
      orParts.push(`site_id.in.(${matchingSiteIds.join(',')})`);
    }
    query = query.or(orParts.join(','));
  }

  const { data, error, count } = await query;
  if (error) throw new Error(error.message);
  return { data: (data ?? []) as unknown as HistoryEvent[], total: count ?? 0 };
}

/** Fetch site extended info + contacts for the event drawer */
export async function fetchSiteWithContacts(siteId: string): Promise<AlarmSiteExtended | null> {
  const { data, error } = await supabase
    .from('alarm_sites')
    .select('*, arc_contacts(*)')
    .eq('id', siteId)
    .single();

  if (error || !data) return null;

  const { arc_contacts, ...site } = data as Record<string, unknown> & { arc_contacts: ArcContact[] };
  return { ...(site as unknown as AlarmSite), contacts: arc_contacts ?? [] } as unknown as AlarmSiteExtended;
}

/** Fetch last N events for a site (for the account drawer history tab) */
export async function fetchSiteRecentEvents(siteId: string, limit = 12): Promise<ArcEvent[]> {
  const { data, error } = await supabase
    .from('arc_events')
    .select('*')
    .eq('site_id', siteId)
    .order('received_at', { ascending: false })
    .limit(limit);

  if (error) return [];
  return data ?? [];
}

/** Fetch active sites list for filter dropdowns */
export async function fetchAllSites(): Promise<Pick<AlarmSite, 'id' | 'name'>[]> {
  const { data, error } = await supabase
    .from('alarm_sites')
    .select('id, name')
    .eq('is_active', true)
    .order('name');

  if (error) return [];
  return (data ?? []) as Pick<AlarmSite, 'id' | 'name'>[];
}

// ─── Site Schedules ───────────────────────────────────────────

/** Returns the full week schedule for a site as a day-keyed map. */
export async function fetchSiteSchedule(siteId: string): Promise<SiteSchedule> {
  const { data, error } = await supabase
    .from('alarm_site_schedules')
    .select('*')
    .eq('site_id', siteId);

  if (error || !data) return {};
  const schedule: SiteSchedule = {};
  for (const row of data as SiteScheduleEntry[]) {
    schedule[row.day] = row;
  }
  return schedule;
}

/** Upsert a single day's schedule window.
 *  Pass open_time/close_time as "HH:MM" strings, or null for a closed day. */
export async function upsertDaySchedule(
  siteId: string,
  day: ScheduleDay,
  open_time: string | null,
  close_time: string | null,
  note?: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('alarm_site_schedules')
    .upsert(
      { site_id: siteId, day, open_time, close_time, note: note ?? null },
      { onConflict: 'site_id,day' },
    );
  if (error) throw new Error(error.message);
}

/** Remove a day from the schedule (reverts to "not configured"). */
export async function deleteDaySchedule(siteId: string, day: ScheduleDay): Promise<void> {
  const { error } = await supabase
    .from('alarm_site_schedules')
    .delete()
    .eq('site_id', siteId)
    .eq('day', day);
  if (error) throw new Error(error.message);
}

// ─── Event Priority ─────────────────────────────────────────

const _CID_SOS_MAP: Record<string, string> = {
  '100': 'medical', '101': 'medical', '102': 'medical',
  '103': 'intrusion', // Hikvision AX PRO's actual interior/perimeter alarm trip code (confirmed fleet-wide) — treat as intrusion, not "other".
  '110': 'fire',    '111': 'fire',    '112': 'fire',    '113': 'fire',
  '114': 'fire',    '115': 'fire',    '116': 'fire',    '117': 'fire',
  '118': 'fire',    '119': 'fire',
  '120': 'panic',   '121': 'panic',   '122': 'panic',   '123': 'panic',
  '124': 'panic',   '125': 'panic',   '126': 'panic',   '127': 'panic',
  '128': 'panic',   '129': 'panic',
  '130': 'intrusion', '131': 'intrusion', '132': 'intrusion', '133': 'intrusion',
  '134': 'intrusion', '135': 'intrusion', '136': 'intrusion', '137': 'intrusion',
  '138': 'intrusion', '139': 'intrusion',
};

/**
 * Returns a numeric priority weight for sorting ARC events (lower = more urgent).
 *
 *  1  Pánico / Robo-Intrusión (CID 120-139, más 103 = disparo interior/perimetral real de Hikvision)
 *  2  Médico          (CID 100-102)
 *  3  Incendio        (CID 110-119)
 *  4  Otras alarmas CRITICAL/ALARM
 *  5  Apertura fuera de horario (open_close + tiempo fuera de 06:00-22:00 GT)
 *  6  Prueba del sistema
 *  7  Apertura normal (disarm, qualifier E)
 *  8  Cierre normal   (arm, qualifier R)
 *  9  Trouble/Falla
 * 10  System/Bypass/Restore
 */
export function getEventPriority(event: {
  event_category: string;
  cid_code: string;
  cid_qualifier: string;
  occurred_at: string;
  received_at: string;
}): number {
  if (event.event_category === 'alarm') {
    const numeric = event.cid_code.replace(/^[ER]/i, '').replace(/^0+/, '');
    const n = parseInt(numeric, 10);
    const base = n >= 1000 ? n % 1000 : n;
    const sos = _CID_SOS_MAP[String(base).padStart(3, '0')];
    if (sos === 'panic' || sos === 'intrusion') return 1;
    if (sos === 'medical')   return 2;
    if (sos === 'fire')      return 3;
    return 4;
  }
  if (event.event_category === 'open_close') {
    try {
      // Guatemala UTC-6, no DST. Out-of-hours heuristic: before 06:00 or after 22:00 local.
      const localMs  = new Date(event.received_at).getTime() - 6 * 60 * 60 * 1000;
      const localHour = new Date(localMs).getUTCHours();
      if (localHour >= 22 || localHour < 6) return 5;  // apertura fuera de horario
    } catch { /* ignore */ }
    return event.cid_qualifier === 'E' ? 7 : 8;  // 7=apertura, 8=cierre
  }
  if (event.event_category === 'test')    return 6;
  if (event.event_category === 'trouble') return 9;
  return 10;  // system, bypass, restore
}

/**
 * Util: given a received_at timestamp (ISO) and an optional schedule + timezone,
 * returns whether the event is outside configured business hours.
 * Returns null when the schedule has no entry for that day (unknown/unconfigured).
 */
export function isOutOfHours(
  receivedAt: string,
  schedule: SiteSchedule,
  _timezone: string, // reserved — currently system is Guatemala-only (fixed UTC-6, no DST)
): boolean | null {
  const DAYS: ScheduleDay[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  try {
    if (!receivedAt) return null;
    const utcMs = new Date(receivedAt).getTime();
    if (isNaN(utcMs)) return null;

    // Guatemala: UTC-6, no DST (fixed offset).
    // Event timestamps come from the server so no client clock correction needed.
    const localMs = utcMs - 6 * 60 * 60 * 1000;
    const local = new Date(localMs);

    const day = DAYS[local.getUTCDay()]; // getUTCDay on offset-adjusted ms = local weekday
    if (!day) return null;

    const entry = schedule[day];
    if (!entry) return null;                           // day not configured → no alert
    if (!entry.open_time || !entry.close_time) return true; // day enabled but closed

    const eventMinutes = local.getUTCHours() * 60 + local.getUTCMinutes();

    // PostgreSQL TIME returns "HH:MM:SS" — split safely, take first two parts
    const [oh, om] = entry.open_time.split(':').map(Number);
    const [ch, cm] = entry.close_time.split(':').map(Number);
    const openMinutes  = oh * 60 + (om || 0);
    const closeMinutes = ch * 60 + (cm || 0);

    return eventMinutes < openMinutes || eventMinutes > closeMinutes;
  } catch {
    return null;
  }
}

// ─── Audit Log ───────────────────────────────────────────────

export interface AuditLogEntry extends ArcEvent {
  alarm_sites: { name: string; account_number: string | null } | null;
  ack_profile: { full_name: string; email: string } | null;
  res_profile: { full_name: string; email: string } | null;
}

export interface AuditFilters {
  dateFrom: string;
  dateTo: string;
  operatorId: string;
  siteId: string;
  severity: string;
}

/** Fetch processed events with operator info for the audit trail page */
export async function fetchAuditLog(
  filters: AuditFilters,
  page = 0,
  pageSize = 100,
): Promise<{ data: AuditLogEntry[]; total: number }> {
  let query = supabase
    .from('arc_events')
    .select(
      '*, alarm_sites ( name, account_number ), ack_profile:profiles!acknowledged_by ( full_name, email ), res_profile:profiles!resolved_by ( full_name, email )',
      { count: 'exact' },
    )
    .not('resolved_at', 'is', null)
    .order('resolved_at', { ascending: false })
    .range(page * pageSize, (page + 1) * pageSize - 1);

  if (filters.dateFrom) {
    query = query.gte('resolved_at', `${filters.dateFrom}T00:00:00`);
  }
  if (filters.dateTo) {
    query = query.lte('resolved_at', `${filters.dateTo}T23:59:59`);
  }
  if (filters.siteId) {
    query = query.eq('site_id', filters.siteId);
  }
  if (filters.severity) {
    query = query.eq('severity', filters.severity);
  }
  if (filters.operatorId) {
    query = query.eq('resolved_by', filters.operatorId);
  }

  const { data, error, count } = await query;
  if (error) throw new Error(error.message);
  return { data: (data ?? []) as unknown as AuditLogEntry[], total: count ?? 0 };
}

/** Fetch list of operators (profiles) for audit filter dropdown */
export async function fetchOperators(): Promise<{ id: string; full_name: string; email: string }[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, email')
    .in('role', ['operator', 'admin', 'super_admin'])
    .order('full_name');

  if (error) return [];
  return (data ?? []) as { id: string; full_name: string; email: string }[];
}
